import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../components/ui/dialog.js";
import { SlidersHorizontal, X } from "../../shell/approved-icons.js";
import { formatLabel } from "../../lib/format.js";
import type {
  ComparisonDetail,
  ComparisonFilters,
} from "../../../shared/contracts/product-pages.js";
import { selectionSummary, teePairLabel } from "./comparison-selection.js";

interface FilterProps {
  filters: ComparisonFilters;
  catalog: ComparisonDetail | null;
  onChange(filters: ComparisonFilters): void;
}
type Choice = { value: string; label: string };

export function ComparisonFilterBar(props: FilterProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="rc-filter-bar">
      <p>{selectionSummary(props.filters)}</p>
      <button
        type="button"
        className="rc-text-button"
        onClick={() => setOpen(true)}
      >
        <SlidersHorizontal size={16} />
        Filters
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rc-filter-sheet" showCloseButton={false}>
          <div className="rc-sheet-heading">
            <DialogTitle>Focus the comparison</DialogTitle>
            <button
              className="rc-icon-button"
              aria-label="Close filters"
              onClick={() => setOpen(false)}
            >
              <X size={20} />
            </button>
          </div>
          <DialogDescription className="sr-only">
            Choose which shared rounds to compare. Agreed results remain
            unchanged.
          </DialogDescription>
          <FilterForm
            key={open ? "open" : "closed"}
            {...props}
            onChange={(filters) => {
              props.onChange(filters);
              setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterForm({
  filters,
  catalog,
  onChange,
}: FilterProps): React.JSX.Element {
  const [draft, setDraft] = useState(filters);
  const fields = filterFields(catalog, filters);
  const set = (key: string, value: string) =>
    setDraft((current) => ({ ...current, [key]: value || undefined }));
  return (
    <form
      className="rc-filter-form"
      onSubmit={(event) => {
        event.preventDefault();
        onChange(draft);
      }}
    >
      <div className="rc-filter-fields">
        {fields.map((field) => (
          <label key={field.key}>
            <span>{field.label}</span>
            <select
              aria-label={field.label}
              value={String(draft[field.key as keyof ComparisonFilters] ?? "")}
              onChange={(event) => set(field.key, event.target.value)}
            >
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
      <div className="rc-actions">
        <button
          type="button"
          className="rc-action"
          onClick={() =>
            setDraft({
              basis: "gross",
              period: filters.period,
              year: filters.year,
              ledger: filters.ledger,
            })
          }
        >
          Reset
        </button>
        <button className="rc-action rc-action-primary" type="submit">
          Apply filters
        </button>
      </div>
    </form>
  );
}

function choices(
  values: string[],
  label: (value: string) => string = (value) => value,
): Choice[] {
  return [...new Set(values)].map((value) => ({ value, label: label(value) }));
}
function all(label: string): Choice {
  return { value: "", label };
}
function filterFields(
  catalog: ComparisonDetail | null,
  filters: ComparisonFilters,
) {
  const environment =
    filters.environment ??
    (filters.ledger === "simulator" ? "simulator" : "outdoor");
  const sources = (catalog?.sources ?? []).filter(
    (source) =>
      source.environment === environment &&
      (filters.period !== "year" || source.calendarYear === filters.year),
  );
  const courses = [
    ...new Map(
      sources.map((row) => [
        row.courseId,
        { value: row.courseId, label: row.courseName },
      ]),
    ).values(),
  ];
  return [
    {
      key: "basis",
      label: "Score lens",
      options: [
        { value: "gross", label: "Gross strokes" },
        { value: "net", label: "Net strokes" },
      ],
    },
    {
      key: "courseId",
      label: "Course",
      options: [all("All courses"), ...courses],
    },
    {
      key: "teePair",
      label: "Tee pairing",
      options: [
        all("All recorded tees"),
        ...choices(
          sources.map((row) => row.teePair),
          teePairLabel,
        ),
      ],
    },
    {
      key: "format",
      label: "Agreed format",
      options: [
        all("All formats"),
        ...choices(
          sources.flatMap((row) => [
            ...row.contests.map((contest) => contest.format),
            ...(row.sharedFormat ? [row.sharedFormat] : []),
          ]),
          (value) =>
            formatLabel(value as NonNullable<ComparisonFilters["format"]>),
        ),
      ],
    },
    ...(environment === "simulator" ? [] : conditionFields(catalog)),
  ];
}

function conditionFields(catalog: ComparisonDetail | null) {
  return [
    {
      key: "transport",
      label: "Transport",
      options: [
        all("Any recorded transport"),
        {
          value: "left-cart",
          label: `${catalog?.relation.left.name ?? "Left party"} rides`,
        },
        {
          value: "right-cart",
          label: `${catalog?.relation.right.name ?? "Right party"} rides`,
        },
        { value: "both-cart", label: "Both ride" },
        { value: "both-walking", label: "Both walk" },
        { value: "mixed", label: "Mixed" },
        { value: "unknown", label: "Unknown" },
      ],
    },
    {
      key: "weather",
      label: "Round weather",
      options: [
        all("Any recorded weather"),
        { value: "rain", label: "Rain recorded for both parties" },
        { value: "unknown", label: "Unknown" },
      ],
    },
  ];
}
